/**
 * 统一用户行动队列的 composable。
 *
 * <p>把「确认卡」和「提问卡」合并为同一条前端 FIFO 展示队列（discriminated union by {@code type}）。
 * 后端通过 V2 持久化待办与过期调度器分别处理提问和确认；前端根据
 * {@code pending_resolved} 事件或刷新后的 {@code /pending} 权威快照更新队列。</p>
 *
 * <p>对外暴露：
 * <ul>
 *   <li>{@code userActionQueue} - 原始队列 ref（写入通过辅助函数）</li>
 *   <li>写入辅助：{@link enqueueUserAction}, {@link clearUserActionQueue},
 *       {@link replaceUserActionOfType}, {@link clearPendingConfirmation}</li>
 *   <li>派生（队头单显）：{@link pendingConfirmation}, {@link pendingUserQuestion}</li>
 *   <li>派生（队列视图）：{@link pendingQuestionQueue}, {@link questionProgress},
 *       {@link isLastQuestion}</li>
 *   <li>派生（按当前 session 过滤）：{@link pendingConfirmationForActiveSession},
 *       {@link pendingUserQuestionForActiveSession}</li>
 * </ul>
 * </p>
 */
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import type {
  PendingGatewayConfirmation,
  PendingUserQuestion,
  UserActionItem,
  UserActionQuestionItem
} from './types'

/** 等价 id：confirmation/question 都用自己的 id 字段。 */
function itemId(item: UserActionItem): string {
  return item.id
}

/** 纯函数：入队（按 id 去重：同 id 的事件视为更新而非新增）。 */
function enqueueUserAction(queue: Ref<UserActionItem[]>, item: UserActionItem): void {
  const idx = queue.value.findIndex((x) => itemId(x) === itemId(item))
  if (idx >= 0) {
    const next = [...queue.value]
    next[idx] = item
    queue.value = next
  } else {
    queue.value = [...queue.value, item]
  }
}

/** 纯函数：清空整条队列。 */
function clearUserActionQueue(queue: Ref<UserActionItem[]>): void {
  queue.value = []
}

/** 纯函数：替换某类型的所有条目。 */
function replaceUserActionOfType(
  queue: Ref<UserActionItem[]>,
  type: 'confirmation' | 'question',
  items: UserActionItem[]
): void {
  const remaining = queue.value.filter((x) => x.type !== type)
  queue.value = [...remaining, ...items]
}

/** 纯函数：仅在队头是 confirmation 时弹出队头（用于"确认/拒绝"完成后清理当前卡片）。 */
function clearPendingConfirmation(queue: Ref<UserActionItem[]>): void {
  if (queue.value[0]?.type === 'confirmation') {
    queue.value = queue.value.slice(1)
  }
}

export function useUserActionQueue(activeSessionId?: () => string): {
  userActionQueue: Ref<UserActionItem[]>
  enqueueUserAction: (item: UserActionItem) => void
  clearUserActionQueue: () => void
  replaceUserActionOfType: (
    type: 'confirmation' | 'question',
    items: UserActionItem[]
  ) => void
  clearPendingConfirmation: () => void
  pendingConfirmation: ComputedRef<PendingGatewayConfirmation | null>
  pendingUserQuestion: ComputedRef<PendingUserQuestion | null>
  pendingQuestionQueue: ComputedRef<PendingUserQuestion[]>
  questionProgress: ComputedRef<{ current: number; total: number } | null>
  isLastQuestion: ComputedRef<boolean>
  canGoPrevQuestion: ComputedRef<boolean>
  currentStagedAnswer: ComputedRef<{ selectedOptionId?: string | null; customAnswer?: string | null } | null>
  goToNextQuestion: () => void
  goToPrevQuestion: () => void
  stageAnswer: (pendingQuestionId: string, answer: { selectedOptionId?: string | null; customAnswer?: string | null }) => void
  getAllStagedAnswers: () => Array<{ pendingQuestionId: string; selectedOptionId?: string | null; customAnswer?: string | null }>
  resetQuestionState: () => void
  filterForActiveSession: <T extends { conversationSessionId: string }>(
    item: T | null,
    activeSid: string
  ) => T | null
} {
  /**
   * 统一的"用户行动队列"：所有等待用户介入的待办（确认卡 + 提问卡）按 FIFO 排队。
   *
   * <p>设计上把原本分开的 {@code pendingConfirmation}（单值 ref）和
   * {@code pendingQuestionQueue}（数组）合并成一条流水线。</p>
   *
   * <p>为了不破坏既有 UI 调用，两个核心派生仍以 computed 形式对外暴露：
   * <ul>
   *   <li>{@code pendingConfirmation} / {@code pendingUserQuestion}
   *       - 队头单显，按 type 取</li>
   *   <li>{@code pendingQuestionQueue} - 全部问题视图（多步进度展示用）</li>
   * </ul>
   * </p>
   *
   * <p>写入策略：</p>
   * <ul>
   *   <li>提问卡：新问题追加到队尾（enqueue，按 id 去重）</li>
   *   <li>确认卡：同一会话只保留最新一张（replaceUserActionOfType）</li>
   * </ul>
   */
  const userActionQueue: Ref<UserActionItem[]> = ref<UserActionItem[]>([])

  /**
   * 会话过滤：提供非空会话 id 时，队头单显/问题队列只看当前会话的条目。
   *
   * <p>队列跨会话共享（后台会话的待办也入队）；若不过滤，其他会话的遗留卡会占据队头，
   * 把当前会话经 SSE 实时送达的卡片永久遮住（历史事故：新会话确认卡只能靠刷新后 /pending 恢复才可见）。</p>
   */
  function matchActiveSession(itemSessionId: string): boolean {
    const sid = activeSessionId?.()?.trim() ?? ''
    if (!sid) {
      return true
    }
    return itemSessionId === sid
  }

  /**
   * 多问题批量提问场景：当前显示的问题索引（0-based）。
   * 单问题场景始终为 0。用户点"下一步"时 +1，点"上一步"时 -1。
   */
  const currentQuestionIndex = ref(0)

  /**
   * 多问题批量提问场景：暂存的答案（按 pendingQuestionId 索引）。
   * 用户选择答案后点"下一步"时暂存，点"提交"时一次性批量提交所有暂存答案。
   */
  const stagedAnswers = ref<Record<string, { selectedOptionId?: string | null; customAnswer?: string | null }>>({})

  /** 派生：当前的确认卡（当前会话的第一张，而非全局队头，避免被其他会话卡片遮挡）。 */
  const pendingConfirmation: ComputedRef<PendingGatewayConfirmation | null> = computed<PendingGatewayConfirmation | null>(() => {
    const item = userActionQueue.value.find(
      (x) => x.type === 'confirmation' && matchActiveSession(x.conversationSessionId)
    )
    if (!item || item.type !== 'confirmation') return null
    const { type: _type, ...rest } = item
    void _type
    return rest as PendingGatewayConfirmation
  })

  /** 派生：当前活跃的问题卡（基于 currentQuestionIndex，仅计当前会话的问题）。 */
  const pendingUserQuestion: ComputedRef<PendingUserQuestion | null> = computed<PendingUserQuestion | null>(() => {
    const questions = userActionQueue.value.filter(
      (x): x is UserActionQuestionItem => x.type === 'question' && matchActiveSession(x.conversationSessionId)
    )
    if (questions.length === 0) return null
    const idx = Math.min(currentQuestionIndex.value, questions.length - 1)
    const item = questions[idx]
    if (!item) return null
    const { type: _type, ...rest } = item
    void _type
    return rest as PendingUserQuestion
  })

  /** 派生：当前会话的问题队列（用于多步问题进度展示；批量作答校验也因此不会误计其他会话的问题）。 */
  const pendingQuestionQueue: ComputedRef<PendingUserQuestion[]> = computed<PendingUserQuestion[]>(() =>
    userActionQueue.value
      .filter((x): x is UserActionQuestionItem => x.type === 'question' && matchActiveSession(x.conversationSessionId))
      .map((x) => {
        const { type: _type, ...rest } = x
        void _type
        return rest as PendingUserQuestion
      })
  )

  const questionProgress: ComputedRef<{ current: number; total: number } | null> = computed<{ current: number; total: number } | null>(() => {
    const q = pendingQuestionQueue.value
    if (q.length === 0) return null
    if (q.length <= 1) return null
    return { current: currentQuestionIndex.value + 1, total: q.length }
  })

  const isLastQuestion: ComputedRef<boolean> = computed<boolean>(() => {
    const q = pendingQuestionQueue.value
    if (q.length === 0) return true
    return currentQuestionIndex.value >= q.length - 1
  })

  /** 是否可以返回上一步（当前不是第一题）。 */
  const canGoPrevQuestion: ComputedRef<boolean> = computed<boolean>(() => {
    return currentQuestionIndex.value > 0
  })

  /** 当前问题的暂存答案（供 QuestionCard 恢复之前的选择）。 */
  const currentStagedAnswer: ComputedRef<{ selectedOptionId?: string | null; customAnswer?: string | null } | null> = computed(() => {
    const q = pendingUserQuestion.value
    if (!q) return null
    return stagedAnswers.value[q.id] ?? null
  })

  /** 暂存当前问题的答案。 */
  function stageAnswer(
    pendingQuestionId: string,
    answer: { selectedOptionId?: string | null; customAnswer?: string | null }
  ): void {
    stagedAnswers.value = { ...stagedAnswers.value, [pendingQuestionId]: answer }
  }

  /** 切换到下一题。 */
  function goToNextQuestion(): void {
    const q = pendingQuestionQueue.value
    if (currentQuestionIndex.value < q.length - 1) {
      currentQuestionIndex.value++
    }
  }

  /** 切换到上一题。 */
  function goToPrevQuestion(): void {
    if (currentQuestionIndex.value > 0) {
      currentQuestionIndex.value--
    }
  }

  /** 获取所有暂存的答案（供批量提交用）。 */
  function getAllStagedAnswers(): Array<{ pendingQuestionId: string; selectedOptionId?: string | null; customAnswer?: string | null }> {
    return Object.entries(stagedAnswers.value).map(([pendingQuestionId, answer]) => ({
      pendingQuestionId,
      ...answer
    }))
  }

  /** 重置多问题状态（批量提交成功后或队列清空时调用）。 */
  function resetQuestionState(): void {
    currentQuestionIndex.value = 0
    stagedAnswers.value = {}
  }

  /**
   * 问题队列清空时自动重置进度索引与暂存答案。
   *
   * <p>上一组问题经 {@code pending_resolved} 逐条出队后，{@code currentQuestionIndex}
   * 仍停留在上一组末题（如 3 题组的索引 2）。若不重置，下一组问题入队后会直接从
   * 该索引开始展示，静默跳过前面的题目，用户会看到错误的题号与进度。</p>
   */
  watch(pendingQuestionQueue, (questions) => {
    if (questions.length === 0) {
      resetQuestionState()
      return
    }
    // 部分摘卡（组内某题被过期决议移除）后钳制索引（fresh-hunter F1）：
    // 否则 currentQuestionIndex 超界会让 questionProgress 显示"第 N+1/M 题"，
    // 且 pendingUserQuestion 的 Math.min 钳制会静默把用户切到另一道题。
    if (currentQuestionIndex.value > questions.length - 1) {
      currentQuestionIndex.value = questions.length - 1
    }
  })

  /**
   * 派生（按 sessionId 过滤）：仅当用户正在查看产生待确认/提问的那条会话时返回对象，
   * 避免切换会话后仍显示 A 的确认/提问面板。
   */
  function filterForActiveSession<T extends { conversationSessionId: string }>(
    item: T | null,
    activeSid: string
  ): T | null {
    if (!item || !activeSid) {
      return null
    }
    return item.conversationSessionId === activeSid ? item : null
  }

  return {
    // 原始状态
    userActionQueue,
    // 写入辅助（已绑定本地 queue）
    enqueueUserAction: (item: UserActionItem) => enqueueUserAction(userActionQueue, item),
    clearUserActionQueue: () => {
      clearUserActionQueue(userActionQueue)
      resetQuestionState()
    },
    replaceUserActionOfType: (
      type: 'confirmation' | 'question',
      items: UserActionItem[]
    ) => {
      replaceUserActionOfType(userActionQueue, type, items)
      if (type === 'question') {
        resetQuestionState()
      }
    },
    clearPendingConfirmation: () => clearPendingConfirmation(userActionQueue),
    // 派生：队头单显
    pendingConfirmation,
    pendingUserQuestion,
    // 派生：队列视图
    pendingQuestionQueue,
    questionProgress,
    isLastQuestion,
    // 多问题分步展示
    canGoPrevQuestion,
    currentStagedAnswer,
    goToNextQuestion,
    goToPrevQuestion,
    stageAnswer,
    getAllStagedAnswers,
    resetQuestionState,
    // 派生：按当前 session 过滤的辅助（供 store 调用）
    filterForActiveSession
  }
}

// 重新导出纯函数（供 streamEvents.ts 等模块无需 import 整个 composable 即可使用）
export {
  enqueueUserAction,
  clearUserActionQueue,
  replaceUserActionOfType,
  clearPendingConfirmation,
  itemId
}
